import { Chip, Tooltip, Typography } from '@mui/material';
import { Briefcase } from 'lucide-react';
import React from 'react';
import { IconWithLabel, TableColumn } from '../../../../ui';
import type { IJobOpeningRow } from '../interfaces';
import { formatDashboardDate } from './formatDashboardDate';

const NUMERIC_TITLES: string[] = ['Applicants', 'Suitable', 'Outstanding', 'Shortlisted', 'Unsuccessful'];

const isDateTitle = (title: string): boolean => title === 'Start Date' || title === 'End Date';

export const getJobOpeningColumns = (onView?: (row: IJobOpeningRow) => void): TableColumn<IJobOpeningRow>[] => {
  const titles: string[] = [
    'Job Title',
    'Job Reference',
    'Applicants',
    'Suitable',
    'Outstanding',
    'Shortlisted',
    'Unsuccessful',
    'Start Date',
    'End Date',
  ];

  return titles.map((title) => ({
    key: title,
    label: title,
    width: title === 'Job Title' ? '240px' : title === 'Job Reference' ? '170px' : '130px',
    align: 'left' as const,
    render: (row: IJobOpeningRow) => {
      if (title === 'Job Title') {
        const value = row['Job Title'] || '—';
        return (
          <IconWithLabel
            label={value}
            icon={<Briefcase size={16} />}
            onClick={onView ? () => onView(row) : undefined}
            maxWidth={200}
          />
        );
      }
      if (title === 'Job Reference') {
        const value = row['Job Reference'];
        if (!value || value === '-') {
          return (
            <Typography variant="body2" color="text.disabled" align="left">
              —
            </Typography>
          );
        }
        return (
          <Chip
            label={value}
            size="small"
            variant="outlined"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.72rem',
              borderRadius: '6px',
              bgcolor: 'grey.50',
            }}
          />
        );
      }
      if (NUMERIC_TITLES.includes(title)) {
        const value = row[title as keyof IJobOpeningRow];
        if (value === undefined || value === null || value === '') {
          return (
            <Typography variant="body2" color="text.disabled" align="left">
              —
            </Typography>
          );
        }
        return (
          <Typography
            variant="body2"
            fontWeight={600}
            align="left"
            sx={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {String(value)}
          </Typography>
        );
      }
      const raw = row[title as keyof IJobOpeningRow];
      const display = isDateTitle(title) ? formatDashboardDate(raw as string) : String(raw ?? '—');
      return (
        <Tooltip title={display} arrow placement="top">
          <Typography variant="body2" noWrap align="left" sx={{ maxWidth: 180 }}>
            {display}
          </Typography>
        </Tooltip>
      );
    },
  }));
};
